import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createVNode as _createVNode, isRef as _isRef, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, withCtx as _withCtx } from "vue";
const _hoisted_1 = { class: "sa-v2" };
const _hoisted_2 = {
    key: 0,
    class: "drillHeader"
};
const _hoisted_3 = ["src"];
const _hoisted_4 = { class: "drillName" };
const _hoisted_5 = { class: "payCycleValue" };
const _hoisted_6 = { class: "drawer-tx-wrap" };
import { ref, computed, onMounted, onUnmounted, watch, nextTick, getCurrentInstance } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAppState } from '@/composables/useAppState';
import { useAuth } from '@/composables/useAuth';
import { useCustomer } from '@/composables/useCustomer';
import { useBanking } from '@/composables/useBanking';
import { usePolling } from '@/composables/usePolling';
import { useContent } from '@/composables/useContent';
import { useVouchers } from '@/composables/useVouchers';
import HeaderBlock from './elements/HeaderBlock.vue';
import AiChatBanner from './elements/AiChatBanner.vue';
import RecentTransactionsSection from './elements/RecentTransactionsSection.vue';
import SimpleDrawerPanel from './elements/SimpleDrawerPanel.vue';
import TransactionsList from './elements/TransactionsList.vue';
import SpendingFilterTabs from './elements/SpendingFilterTabs.vue';
import PeriodSelector from './elements/PeriodSelector.vue';
import DateRangeDrawer from './elements/DateRangeDrawer.vue';
import AccountSelector from './elements/AccountSelector.vue';
import SpendingSummaryCard from './elements/SpendingSummaryCard.vue';
import CategoryAnalysisList from './elements/CategoryAnalysisList.vue';
import UncategorisedCard from './elements/UncategorisedCard.vue';
import BudgetSummaryCard from './elements/BudgetSummaryCard.vue';
import InfoPanel from './elements/InfoPanel.vue';
import SuccessToast from './elements/SuccessToast.vue';
import { selectedFilter, selectedPeriod, selectedAccount, selectedSubCategory, selectedSubCategoryColour, selectedMerchant, dateSelection, snapToSpendingCycle, cameFromTransactionDetail, } from './elements/spendingAnalysisFilterState';
import { getTransactionClassificationTree, getSpendingAnalysis, buildCategoryRows, buildTransactionGroups, groupTransactionsByDay, formatTimestampString, findCurrentMonthIndex, getMonthRangeForSelection, SPENDING_BREAKDOWN_PALETTE } from '@/composables/useSpendingAnalysis';
import { formatMonthYearShort, currentTaxYearLabel, formatResponseRange, getMerchantLogoUrl, getMerchantLogoStyle, isWellKnownLogosOnly, onMerchantLogoError } from '@/utils/helpers';
import { downloadSpendingReport } from '@/composables/spendingReport';
// ---------- Constants ----------
export default /*@__PURE__*/ _defineComponent({
    __name: 'SpendingAnalysisPage',
    setup(__props) {
        const PERIODS = [
            { key: 'month', label: 'Month' },
            { key: 'Year', label: 'Year' },
            { key: 'tax', label: currentTaxYearLabel() },
        ];
        // ---------- Composables ----------
        const state = useAppState();
        const router = useRouter();
        const route = useRoute();
        useAuth();
        const { getCustomer } = useCustomer();
        const { getBankingInfo, getRecurringPayments } = useBanking();
        const { poll: pollBanking } = usePolling('saBankingAfterBulk');
        const { getPlans } = useContent();
        const { getAvailableVouchers, attachAvailableVouchers } = useVouchers();
        // ---------- Reactive state ----------
        const filterTabs = ref([{ key: 'all', label: 'All' }]);
        const periods = ref(PERIODS);
        const summary = ref(null);
        const takebackPlan = computed(() => state.plans?.find((p) => p.tagLine === 'takeback') ?? null);
        const guidesHabits = computed(() => {
            const p = takebackPlan.value;
            if (!p)
                return [];
            return [{
                    id: p.id,
                    category: 'Plan',
                    title: p.title,
                    description: p.description,
                    unread: false,
                    image: require('@/assets/SpendingAnalysisGuide.png'),
                }];
        });
        const guidesDeepDive = computed(() => (state.plans ?? [])
            .filter((p) => typeof p.tagLine === 'string' && p.tagLine.startsWith('myth#'))
            .map((p) => ({
            id: p.id,
            category: p.tagLine,
            title: p.title,
            unread: false,
            image: require('@/assets/SpendingAnalysisGuideSections.png'),
        })));
        const transactionsDrawerOpen = ref(false);
        const dateDrawerOpen = ref(false);
        const categorisedToastCount = ref(0);
        // Gates fetches until banking info has loaded, so analysisRange's
        // spendingCycle-aware snap is settled before the first call.
        const fetchReady = ref(false);
        const summaryLoading = ref(true);
        const recurringPayments = ref(null);
        // ---------- Computeds: filter / view ----------
        const selectedAccountIds = computed(() => {
            const key = selectedAccount.value;
            const accounts = (state.bankInfo?.accounts || []).filter((a) => a.source === 0);
            if (!accounts.length || key === 'all')
                return undefined;
            const typeMap = { current: 'Current', credit: 'Credit', savings: 'Savings' };
            if (typeMap[key]) {
                const ids = accounts.filter((a) => a.accountType?.prettyName === typeMap[key]).map((a) => a.id);
                return ids.length ? ids : undefined;
            }
            const match = accounts.find((a) => a.id === key);
            return match ? [match.id] : undefined;
        });
        const isAllView = computed(() => selectedFilter.value === 'all' && !selectedSubCategory.value && !selectedMerchant.value);
        const isDrilledIn = computed(() => !!(selectedSubCategory.value || selectedMerchant.value));
        const wellKnownLogosOnlyGlobal = getCurrentInstance()?.appContext.config.globalProperties.$wellKnownLogosOnly;
        const drillMerchantInfo = computed(() => {
            if (!selectedMerchant.value)
                return null;
            const parents = Array.isArray(summary.value?.parentCategories) ? summary.value.parentCategories : [];
            for (const p of parents) {
                const merchants = Array.isArray(p.spendByMerchant) ? p.spendByMerchant : [];
                for (const m of merchants) {
                    if (m.merchantName !== selectedMerchant.value)
                        continue;
                    const wellKnown = Array.isArray(m.transactions) && m.transactions.some((t) => t.wellKnownMerchant);
                    const iconPath = m.transactions?.[0]?.icon ?? p.transactions?.[0]?.icon;
                    const iconColour = m.transactions?.[0]?.color ?? p.transactions?.[0]?.color ?? p.color;
                    return { wellKnown, iconPath, iconColour };
                }
            }
            return { wellKnown: false, iconPath: undefined, iconColour: undefined };
        });
        const drillCategoryIconPath = computed(() => {
            if (!selectedSubCategory.value)
                return undefined;
            const cats = Array.isArray(summary.value?.categories) ? summary.value.categories : [];
            const c = cats.find((x) => x.subCategory === selectedSubCategory.value);
            return c?.iconPath ?? c?.icon ?? c?.transactions?.[0]?.icon;
        });
        const drillAvatarMode = computed(() => {
            if (selectedMerchant.value) {
                const info = drillMerchantInfo.value;
                if (isWellKnownLogosOnly(wellKnownLogosOnlyGlobal) && !info?.wellKnown)
                    return 'merchant-fallback';
                return 'merchant-logo';
            }
            return 'category';
        });
        function getCategoryIconUrl(iconPath) {
            if (!iconPath)
                return require('@/assets/transactions/home.png');
            try {
                const base = iconPath.toLowerCase().replace('.svg', '');
                return require('@/assets/transactions/' + base + '.png');
            }
            catch (e) {
                return require('@/assets/transactions/home.png');
            }
        }
        const drillAvatarSrc = computed(() => {
            if (drillAvatarMode.value === 'merchant-logo') {
                const info = drillMerchantInfo.value;
                return getMerchantLogoUrl({
                    name: selectedMerchant.value,
                    wellKnown: !!info?.wellKnown,
                    iconPath: info?.iconPath,
                    iconColour: info?.iconColour,
                    wellKnownLogosOnly: isWellKnownLogosOnly(wellKnownLogosOnlyGlobal),
                });
            }
            if (drillAvatarMode.value === 'merchant-fallback') {
                return getCategoryIconUrl(drillMerchantInfo.value?.iconPath);
            }
            return getCategoryIconUrl(drillCategoryIconPath.value);
        });
        const drillAvatarStyle = computed(() => {
            if (drillAvatarMode.value === 'merchant-logo') {
                const info = drillMerchantInfo.value;
                return getMerchantLogoStyle({
                    name: selectedMerchant.value,
                    wellKnown: !!info?.wellKnown,
                    iconPath: info?.iconPath,
                    iconColour: info?.iconColour,
                    wellKnownLogosOnly: isWellKnownLogosOnly(wellKnownLogosOnlyGlobal),
                });
            }
            if (drillAvatarMode.value === 'merchant-fallback') {
                return { backgroundColor: drillMerchantInfo.value?.iconColour || selectedFilterColour.value };
            }
            return { backgroundColor: selectedFilterColour.value };
        });
        function onDrillAvatarError(e) {
            if (drillAvatarMode.value === 'merchant-logo') {
                onMerchantLogoError(e, { iconPath: drillMerchantInfo.value?.iconPath });
                return;
            }
            ;
            e.target.src = require('@/assets/transactions/home.png');
        }
        const selectedTab = computed(() => filterTabs.value.find(t => t.key === selectedFilter.value));
        const selectedCategory = computed(() => (selectedTab.value && selectedTab.value.key !== 'all') ? selectedTab.value.label : undefined);
        const selectedFilterLabel = computed(() => selectedMerchant.value || selectedSubCategory.value || selectedTab.value?.label || '');
        const categoryHeaderAmount = computed(() => {
            // Hide while a fetch is in flight so we don't flash the previous
            // category's totals for the new selection.
            if (summaryLoading.value)
                return undefined;
            const r = summary.value;
            if (!r)
                return undefined;
            const inAmt = r.totalIn ?? 0;
            const out = r.totalOut ?? 0;
            return inAmt - out;
        });
        const transactionsSectionTitle = computed(() => {
            const label = selectedMerchant.value || selectedSubCategory.value || (selectedTab.value && selectedTab.value.key !== 'all' ? selectedTab.value.label : '');
            return label ? `Transactions - ${label}` : 'Transactions';
        });
        const selectedFilterColour = computed(() => {
            if (selectedSubCategory.value) {
                // Captured at click time so colour is stable across refetches.
                return selectedSubCategoryColour.value || SPENDING_BREAKDOWN_PALETTE[0];
            }
            if (selectedTab.value?.color)
                return selectedTab.value.color;
            const parent = (summary.value?.parentCategories ?? []).find((p) => p.name === selectedFilterLabel.value);
            const txColour = parent?.categoryColor ?? parent?.transactions?.[0]?.categoryColor ?? parent?.color ?? parent?.transactions?.[0]?.color;
            return txColour ?? SPENDING_BREAKDOWN_PALETTE[0];
        });
        // ---------- Computeds: data shaping ----------
        // Month window for the monthly bars. Driven by the date-range selection
        // (months / calendarYear / taxYear) or the period preset (Year / tax).
        // Returns null for the `month` preset (that view renders weekly bars instead).
        const monthRange = computed(() => {
            return getMonthRangeForSelection(dateSelection.value, selectedPeriod.value);
        });
        const analysisRange = computed(() => {
            if (monthRange.value) {
                const [fy, fm] = monthRange.value.startMonth.split('-').map(Number);
                const [ty, tm] = monthRange.value.endMonth.split('-').map(Number);
                return {
                    fromDate: new Date(Date.UTC(fy, fm - 1, 1)),
                    toDate: new Date(Date.UTC(ty, tm, 1)),
                };
            }
            return { fromDate: null, toDate: null };
        });
        const categorySliceBars = computed(() => {
            const slices = summary.value?.timeSlicedBreakdowns;
            if (!Array.isArray(slices))
                return [];
            const now = Date.now();
            return slices.map((s) => {
                const from = s.fromDate ? new Date(s.fromDate).getTime() : 0;
                return {
                    label: s.label,
                    value: Math.abs((s.totalOut ?? 0) - (s.totalIn ?? 0)),
                    isFuture: from > now,
                    fromDate: s.fromDate,
                    toDate: s.toDate,
                };
            });
        });
        const dataIsWeekly = computed(() => {
            const slices = summary.value?.timeSlicedBreakdowns;
            return Array.isArray(slices) && slices.length > 0 && slices.every((s) => s.week === true);
        });
        const currentMonthIndex = computed(() => findCurrentMonthIndex(categorySliceBars.value));
        const currentCategories = computed(() => buildCategoryRows(summary.value, isAllView.value, selectedSubCategory.value, selectedSubCategoryColour.value));
        const currentMerchants = computed(() => {
            const parents = Array.isArray(summary.value?.parentCategories) ? summary.value.parentCategories : [];
            const byName = new Map();
            for (const p of parents) {
                const merchants = Array.isArray(p.spendByMerchant) ? p.spendByMerchant : [];
                for (const m of merchants) {
                    const totalIn = m.totalIn ?? 0;
                    const totalOut = m.totalOut ?? 0;
                    const amount = Math.abs(totalOut > totalIn ? totalOut : totalIn);
                    if (amount <= 0)
                        continue;
                    // Merchant-level well-known flag isn't on the aggregate — infer it from
                    // any child transaction's wellKnownMerchant (all txs for a merchant share it).
                    const wellKnown = Array.isArray(m.transactions) && m.transactions.some((t) => t.wellKnownMerchant);
                    const iconPath = m.transactions?.[0]?.icon ?? p.transactions?.[0]?.icon;
                    const iconColour = m.transactions?.[0]?.color ?? p.transactions?.[0]?.color ?? p.color;
                    const existing = byName.get(m.merchantName);
                    if (existing) {
                        existing.amount += amount;
                        existing.transactionCount += m.transactionCount ?? 0;
                        if (wellKnown)
                            existing.merchant = true;
                        if (!existing.iconPath && iconPath)
                            existing.iconPath = iconPath;
                        if (!existing.iconColour && iconColour)
                            existing.iconColour = iconColour;
                    }
                    else {
                        byName.set(m.merchantName, {
                            name: m.merchantName,
                            transactionCount: m.transactionCount ?? 0,
                            amount,
                            merchant: wellKnown ? true : undefined,
                            iconPath,
                            iconColour,
                            isIncome: totalIn > totalOut,
                        });
                    }
                }
            }
            return [...byName.values()].sort((a, b) => b.amount - a.amount);
        });
        const rangeLabel = computed(() => {
            const r = summary.value;
            if (r?.fromDate && r?.toDate) {
                return formatResponseRange(r.fromDate, r.toDate);
            }
            const sel = dateSelection.value;
            if (sel) {
                if (sel.type === 'months' && sel.startMonth) {
                    const start = formatMonthYearShort(sel.startMonth);
                    if (!sel.endMonth || sel.endMonth === sel.startMonth)
                        return start;
                    return `${start} - ${formatMonthYearShort(sel.endMonth)}`;
                }
                return sel.label ?? '';
            }
            if (selectedPeriod.value === 'month')
                return 'This month';
            if (selectedPeriod.value === 'Year')
                return 'This year';
            if (selectedPeriod.value === 'tax')
                return 'This tax year';
            return '';
        });
        // Shaped breakdown for the All-view SpendingSummaryCard (overview mode).
        // Computed off `summary` so it stays in sync with refetches and re-applies
        // colours when the classification tree resolves.
        const spendingSummary = computed(() => {
            const r = summary.value;
            if (!r || !isAllView.value) {
                return { rangeLabel: rangeLabel.value, net: 0, totalIn: 0, totalOut: 0, breakdown: [] };
            }
            // Per-parent semantics:
            //   Income       → gross totalIn
            //   non-Income   → net outflow (totalOut − totalIn), so refunds/cashback
            //                  shrink the category's segment and row.
            // Income asymmetry is deliberate: an outflow within the Income parent
            // (e.g. salary clawback) does not currently reduce the In bar.
            const parents = Array.isArray(r.parentCategories) ? r.parentCategories : [];
            const breakdown = parents
                .map((p, i) => {
                const isIncome = p.name === 'Income';
                const amount = isIncome ? (p.totalIn ?? 0) : ((p.totalOut ?? 0) - (p.totalIn ?? 0));
                return {
                    name: p.name,
                    amount,
                    colour: filterTabs.value.find(t => t.label === p.name)?.color
                        ?? SPENDING_BREAKDOWN_PALETTE[i % SPENDING_BREAKDOWN_PALETTE.length],
                    direction: (isIncome ? 'in' : 'out'),
                };
            })
                .filter(b => b.amount > 0);
            const incomeIn = breakdown.find(b => b.name === 'Income' && b.direction === 'in')?.amount ?? 0;
            const totalOut = breakdown
                .filter(b => b.direction === 'out')
                .reduce((sum, b) => sum + b.amount, 0);
            return {
                rangeLabel: rangeLabel.value,
                net: incomeIn - totalOut,
                totalIn: incomeIn,
                totalOut,
                breakdown,
            };
        });
        const apiTransactionGroups = computed(() => {
            const groups = buildTransactionGroups(summary.value);
            attachAvailableVouchers(groups);
            return groups;
        });
        function isUncategorised(t) {
            return !t.subClassification || t.subClassification === 'Uncategorised' || t.subClassification === 'Uncategorized';
        }
        const uncategorisedTransactionGroups = computed(() => {
            const parents = Array.isArray(summary.value?.parentCategories) ? summary.value.parentCategories : [];
            const flat = [];
            const seen = new Set();
            for (const p of parents) {
                const txs = Array.isArray(p.transactions) ? p.transactions : [];
                for (const t of txs) {
                    if (!isUncategorised(t))
                        continue;
                    const id = t.id ?? `${t.merchantName}-${t.timestamp}-${t.amount}`;
                    if (seen.has(id))
                        continue;
                    seen.add(id);
                    flat.push({
                        id,
                        merchantName: t.merchantName,
                        amount: t.amount,
                        timestamp: t.timestamp,
                        timestampString: formatTimestampString(t.timestamp),
                        wellKnownMerchant: t.wellKnownMerchant,
                        effectiveClassification: {
                            mgClassification: t.classification,
                            mgSubClassification: t.subClassification,
                            color: p.color ?? t.color,
                            iconPath: t.icon,
                        },
                    });
                }
            }
            return groupTransactionsByDay(flat);
        });
        const uncategorisedCount = computed(() => uncategorisedTransactionGroups.value.reduce((sum, g) => sum + g.length, 0));
        const monthCount = computed(() => {
            const r = monthRange.value;
            if (!r)
                return 1;
            const [fy, fm] = r.startMonth.split('-').map(Number);
            const [ty, tm] = r.endMonth.split('-').map(Number);
            return (ty - fy) * 12 + (tm - fm) + 1;
        });
        const subCategoryBudgets = computed(() => {
            if (selectedMerchant.value)
                return [];
            const isSubCat = !!selectedSubCategory.value;
            const cats = summary.value?.categories ?? [];
            const matches = cats.filter((c) => {
                if (!c.plan)
                    return false;
                if (isSubCat)
                    return c.subCategory === selectedSubCategory.value;
                return true;
            });
            const to = summary.value?.toDate ? new Date(summary.value.toDate) : null;
            const resetDate = to ? to.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' }) : '';
            return matches.map((c) => ({
                categoryName: c.subCategory,
                parentName: c.name ?? c.classification ?? '',
                currentSpend: Math.abs(c.totalOut ?? 0),
                planAmount: c.plan.amount * monthCount.value,
                resetDate,
            }));
        });
        const payCycleLabel = computed(() => {
            const day = state.bankInfo?.spendingCycle?.day;
            if (!day)
                return 'Not set';
            const s = ['th', 'st', 'nd', 'rd'];
            const v = day % 100;
            const suffix = s[(v - 20) % 10] || s[v] || s[0];
            return `Monthly on ${day}${suffix}`;
        });
        // ---------- Fetch ----------
        // Loads the spending analysis for the current range, accounts, and drilldown
        // filter (parent category / sub-category / merchant). One ref, refetched on
        // every relevant change — no client-side derived caches.
        function fetchSummary() {
            const { fromDate, toDate } = analysisRange.value;
            const opts = {
                accountIds: selectedAccountIds.value,
                snapToSpendingCycle: snapToSpendingCycle.value,
            };
            if (selectedMerchant.value)
                opts.merchant = selectedMerchant.value;
            else if (selectedSubCategory.value)
                opts.subCategory = selectedSubCategory.value;
            else if (selectedCategory.value)
                opts.category = selectedCategory.value;
            summaryLoading.value = true;
            getSpendingAnalysis(fromDate, toDate, opts, (result) => {
                if (result)
                    summary.value = result;
                summaryLoading.value = false;
                if (selectedSubCategory.value === 'Uncategorised' && uncategorisedCount.value === 0) {
                    selectedSubCategory.value = '';
                    selectedFilter.value = 'all';
                }
            });
        }
        // ---------- Event handlers ----------
        function openTransactionsDrawer() {
            transactionsDrawerOpen.value = true;
        }
        function onDateRangeSelect(selection) {
            dateSelection.value = selection;
        }
        function onClearDateSelection() {
            dateSelection.value = null;
            selectedPeriod.value = 'month';
        }
        function onMonthBarClick(bar) {
            if (!bar.fromDate)
                return;
            const d = new Date(bar.fromDate);
            if (Number.isNaN(d.getTime()))
                return;
            const y = d.getUTCFullYear();
            const m = String(d.getUTCMonth() + 1).padStart(2, '0');
            const monthKey = `${y}-${m}`;
            dateSelection.value = {
                type: 'months',
                startMonth: monthKey,
                endMonth: monthKey,
                label: formatMonthYearShort(monthKey),
            };
            selectedPeriod.value = 'month';
        }
        function onCategoryClick(name) {
            const tab = filterTabs.value.find(t => t.label === name);
            if (tab)
                selectedFilter.value = tab.key;
        }
        function onSubCategoryClick(name, colour) {
            selectedSubCategory.value = name;
            selectedSubCategoryColour.value = colour || '';
        }
        function onMerchantClick(name) {
            selectedMerchant.value = name;
        }
        function goToTransaction(t) {
            router.push('/TransactionDetail/' + t.id);
        }
        const generatingReport = ref(false);
        function onGenerateReport() {
            if (generatingReport.value)
                return;
            generatingReport.value = true;
            const { fromDate, toDate } = analysisRange.value;
            getSpendingAnalysis(fromDate, toDate, {
                accountIds: selectedAccountIds.value,
                snapToSpendingCycle: snapToSpendingCycle.value,
            }, async (result) => {
                if (!result) {
                    generatingReport.value = false;
                    return;
                }
                try {
                    await downloadSpendingReport(result, {
                        filterTabs: filterTabs.value,
                        recurringPayments: recurringPayments.value?.payments ?? [],
                        formatter: state.formatter,
                        wellKnownLogosOnly: wellKnownLogosOnlyGlobal,
                        fallbackRangeLabel: rangeLabel.value,
                    });
                }
                finally {
                    generatingReport.value = false;
                }
            });
        }
        function goToBulkCategorise() {
            // Prefer the server-resolved range (already cycle-snapped) so the bulk page
            // queries the exact window the count was based on. Falls back to the local
            // analysisRange while the first fetch is still in flight.
            const r = summary.value;
            const from = r?.fromDate ?? analysisRange.value.fromDate;
            const to = r?.toDate ?? analysisRange.value.toDate;
            const query = {};
            if (from)
                query.from = typeof from === 'string' ? from : from.toISOString();
            if (to)
                query.to = typeof to === 'string' ? to : to.toISOString();
            router.push({ path: '/TransactionBulk', query });
        }
        function onDrawerTransactionClick(t) {
            sessionStorage.setItem('reopenTransactionsDrawer', 'true');
            goToTransaction(t);
        }
        function onGuideClick(guide) {
            router.push('/Guides/' + guide.id);
        }
        function goToAllGuides() {
            router.push('/guides');
        }
        function onBack() {
            if (cameFromTransactionDetail.value) {
                cameFromTransactionDetail.value = false;
                router.back();
                return;
            }
            if (selectedMerchant.value) {
                selectedMerchant.value = '';
            }
            else if (selectedSubCategory.value) {
                selectedSubCategory.value = '';
            }
            else if (selectedFilter.value !== 'all') {
                selectedFilter.value = 'all';
            }
            else {
                router.back();
                return;
            }
            window.scrollTo({ top: 0 });
        }
        // ---------- Lifecycle ----------
        onMounted(() => {
            const toastRaw = sessionStorage.getItem('saCategorisedToast');
            if (toastRaw) {
                sessionStorage.removeItem('saCategorisedToast');
                const n = parseInt(toastRaw, 10);
                if (!Number.isNaN(n) && n > 0) {
                    categorisedToastCount.value = n;
                    // Bank info is stale after bulk tagging — backend takes a moment to
                    // reflect the changes. Poll until untaggedTransactionsCurrentCycleCount
                    // drops (or we run out of attempts).
                    const before = state.bankInfo?.untaggedTransactionsCurrentCycleCount ?? null;
                    pollBanking({
                        remaining: 5,
                        interval: 1500,
                        fetch: (cb) => getBankingInfo(cb, /* refreshCache */ true),
                        onPoll: () => { },
                        shouldStop: () => {
                            const now = state.bankInfo?.untaggedTransactionsCurrentCycleCount;
                            return before === null || (typeof now === 'number' && now !== before);
                        },
                        onFinish: () => { },
                    });
                }
            }
            if (sessionStorage.getItem('reopenTransactionsDrawer') === 'true') {
                transactionsDrawerOpen.value = true;
                sessionStorage.removeItem('reopenTransactionsDrawer');
            }
            getPlans();
            getAvailableVouchers();
            getRecurringPayments((res) => {
                recurringPayments.value = res;
            });
            getCustomer(() => {
                getBankingInfo(() => {
                    getTransactionClassificationTree((tree) => {
                        if (tree?.parents) {
                            const apiTabs = tree.parents.map((p) => ({
                                key: p.parent?.key ?? p.name,
                                label: p.name,
                                categoryId: p.parent?.id,
                                color: p.categoryColor ?? p.parent?.categoryColor ?? p.color ?? p.parent?.color,
                            }));
                            const order = ['Income', 'Essentials', 'Lifestyle', 'Future', 'General'];
                            apiTabs.sort((a, b) => {
                                const ai = order.indexOf(a.label);
                                const bi = order.indexOf(b.label);
                                return (ai === -1 ? order.length : ai) - (bi === -1 ? order.length : bi);
                            });
                            filterTabs.value = [{ key: 'all', label: 'All' }, ...apiTabs];
                        }
                        fetchReady.value = true;
                        fetchSummary();
                    });
                });
            });
        });
        // Sync watcher — flips loading on the SAME tick the filter changes so the
        // empty-state overlay can never appear between the click and the async fetch.
        watch([analysisRange, selectedAccountIds, snapToSpendingCycle, selectedFilter, selectedSubCategory, selectedMerchant], () => {
            summaryLoading.value = true;
        }, { flush: 'sync' });
        watch([analysisRange, selectedAccountIds, snapToSpendingCycle, selectedFilter, selectedSubCategory, selectedMerchant], () => {
            if (!fetchReady.value)
                return;
            fetchSummary();
        });
        watch([selectedFilter, selectedSubCategory, selectedMerchant], () => {
            window.scrollTo({ top: 0 });
        });
        function syncNavBar() {
            const drilled = !!(selectedSubCategory.value || selectedMerchant.value);
            document.body.classList.toggle('hide-nav-bar', drilled);
        }
        watch([selectedSubCategory, selectedMerchant], syncNavBar);
        onMounted(() => {
            // Defer past App.vue's `route.path` watcher which strips `hide-nav-bar` on every nav.
            nextTick(syncNavBar);
        });
        onUnmounted(() => {
            document.body.classList.remove('hide-nav-bar');
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createVNode(HeaderBlock, {
                    title: isDrilledIn.value ? (_unref(selectedMerchant) ? 'Merchant' : 'Category') : 'Spending Analysis',
                    logicalBack: true,
                    onLogicalBack: onBack,
                    onDownload: onGenerateReport
                }, null, 8, ["title"]),
                (!_unref(selectedSubCategory) && !_unref(selectedMerchant))
                    ? (_openBlock(), _createBlock(SpendingFilterTabs, {
                        key: 0,
                        modelValue: _unref(selectedFilter),
                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => (_isRef(selectedFilter) ? (selectedFilter).value = $event : null)),
                        tabs: filterTabs.value,
                        class: "sa-filter-tabs"
                    }, null, 8, ["modelValue", "tabs"]))
                    : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_1, [
                    (isDrilledIn.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                            _createElementVNode("div", {
                                class: "drillAvatar",
                                style: _normalizeStyle(drillAvatarStyle.value)
                            }, [
                                _createElementVNode("img", {
                                    src: drillAvatarSrc.value,
                                    class: _normalizeClass(drillAvatarMode.value === 'merchant-logo' ? 'drillAvatarImgLogo' : 'drillAvatarImgIcon'),
                                    onError: onDrillAvatarError,
                                    alt: ""
                                }, null, 42, _hoisted_3)
                            ], 4),
                            _createElementVNode("div", _hoisted_4, _toDisplayString(_unref(selectedMerchant) || _unref(selectedSubCategory)), 1)
                        ]))
                        : _createCommentVNode("", true),
                    _createVNode(PeriodSelector, {
                        modelValue: _unref(selectedPeriod),
                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => (_isRef(selectedPeriod) ? (selectedPeriod).value = $event : null)),
                        periods: periods.value,
                        dateSelection: _unref(dateSelection),
                        onOpenDrawer: _cache[2] || (_cache[2] = ($event) => (dateDrawerOpen.value = true)),
                        onClearSelection: onClearDateSelection
                    }, null, 8, ["modelValue", "periods", "dateSelection"]),
                    (!isDrilledIn.value)
                        ? (_openBlock(), _createElementBlock("div", {
                            key: 1,
                            class: "payCycleRow",
                            onClick: _cache[3] || (_cache[3] = ($event) => (_unref(router).push('/setupspendinganalysis')))
                        }, [
                            _cache[12] || (_cache[12] = _createElementVNode("div", { class: "payCycleLabel" }, [
                                _createElementVNode("div", { class: "payCycleIcon" }),
                                _createElementVNode("span", null, "Pay cycle date")
                            ], -1)),
                            _createElementVNode("div", _hoisted_5, [
                                _createElementVNode("span", null, _toDisplayString(payCycleLabel.value), 1),
                                _cache[11] || (_cache[11] = _createElementVNode("div", { class: "payCycleEdit" }, null, -1))
                            ])
                        ]))
                        : _createCommentVNode("", true),
                    (!isDrilledIn.value)
                        ? (_openBlock(), _createBlock(AccountSelector, {
                            key: 2,
                            modelValue: _unref(selectedAccount),
                            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => (_isRef(selectedAccount) ? (selectedAccount).value = $event : null))
                        }, null, 8, ["modelValue"]))
                        : _createCommentVNode("", true),
                    (isAllView.value)
                        ? (_openBlock(), _createBlock(SpendingSummaryCard, {
                            key: "overview",
                            data: spendingSummary.value,
                            loading: summaryLoading.value,
                            onCategoryClick: onCategoryClick
                        }, null, 8, ["data", "loading"]))
                        : (_openBlock(), _createBlock(SpendingSummaryCard, {
                            key: 'category-' + (_unref(selectedMerchant) || _unref(selectedSubCategory) || _unref(selectedFilter)),
                            mode: "category",
                            categoryLabel: selectedFilterLabel.value,
                            rangeLabel: rangeLabel.value,
                            amount: categoryHeaderAmount.value,
                            monthlyData: categorySliceBars.value,
                            weeklyData: dataIsWeekly.value ? categorySliceBars.value : undefined,
                            currentMonthIndex: currentMonthIndex.value,
                            barColour: selectedFilterColour.value,
                            loading: summaryLoading.value,
                            onMonthClick: onMonthBarClick
                        }, null, 8, ["categoryLabel", "rangeLabel", "amount", "monthlyData", "weeklyData", "currentMonthIndex", "barColour", "loading"])),
                    (isAllView.value)
                        ? (_openBlock(), _createBlock(AiChatBanner, {
                            key: 5,
                            class: "aiChat"
                        }))
                        : _createCommentVNode("", true),
                    (!isDrilledIn.value && (currentCategories.value.length > 0 || currentMerchants.value.length > 0))
                        ? (_openBlock(), _createBlock(CategoryAnalysisList, {
                            key: _unref(selectedSubCategory) || _unref(selectedFilter) || 'all',
                            categories: currentCategories.value,
                            merchants: currentMerchants.value,
                            showMerchants: !_unref(selectedMerchant),
                            initialTab: _unref(selectedSubCategory) ? 'merchants' : 'analysis',
                            dateRange: rangeLabel.value,
                            onCategoryClick: onSubCategoryClick,
                            onMerchantClick: onMerchantClick
                        }, null, 8, ["categories", "merchants", "showMerchants", "initialTab", "dateRange"]))
                        : _createCommentVNode("", true),
                    (!summaryLoading.value && uncategorisedCount.value > 0)
                        ? (_openBlock(), _createBlock(UncategorisedCard, {
                            key: 7,
                            count: uncategorisedCount.value,
                            class: "uncategorisedCard",
                            onClick: goToBulkCategorise
                        }, null, 8, ["count"]))
                        : _createCommentVNode("", true),
                    (!isDrilledIn.value && isAllView.value)
                        ? (_openBlock(), _createBlock(InfoPanel, {
                            key: 8,
                            href: "/#/SpendingTreemap",
                            icon: require('@/assets/spendingtreemap2.png'),
                            title: "Where your money goes",
                            showArrow: ""
                        }, null, 8, ["icon"]))
                        : _createCommentVNode("", true),
                    (apiTransactionGroups.value.length > 0)
                        ? (_openBlock(), _createBlock(RecentTransactionsSection, {
                            key: 9,
                            transactions: apiTransactionGroups.value,
                            formatter: _unref(state).formatter,
                            today: _unref(state).today,
                            yesterday: _unref(state).yesterday,
                            accountType: "all",
                            title: transactionsSectionTitle.value,
                            onTransactionClick: goToTransaction,
                            categoryPills: true,
                            onViewMore: openTransactionsDrawer,
                            limit: 3
                        }, null, 8, ["transactions", "formatter", "today", "yesterday", "title"]))
                        : _createCommentVNode("", true),
                    (!isDrilledIn.value && recurringPayments.value && recurringPayments.value.payments.length > 0)
                        ? (_openBlock(), _createBlock(InfoPanel, {
                            key: 10,
                            href: "/#/RecurringPayments",
                            icon: require('@/assets/coins.png'),
                            title: `${recurringPayments.value.payments.length} Recurring payments`,
                            subtitle: "Stay on top of your payments",
                            showArrow: ""
                        }, null, 8, ["icon", "title"]))
                        : _createCommentVNode("", true),
                    (!_unref(selectedMerchant))
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 11 }, [
                            (subCategoryBudgets.value.length === 0)
                                ? (_openBlock(), _createBlock(BudgetSummaryCard, {
                                    key: 0,
                                    empty: "",
                                    onClick: _cache[5] || (_cache[5] = ($event) => (_unref(router).push({ path: '/SpendingPlan', query: { section: selectedTab.value?.label ?? 'all' } })))
                                }))
                                : (!_unref(selectedSubCategory))
                                    ? (_openBlock(), _createElementBlock("div", {
                                        key: 1,
                                        class: _normalizeClass(["budgetStack", { tight: subCategoryBudgets.value.length > 1 }])
                                    }, [
                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(subCategoryBudgets.value, (b) => {
                                            return (_openBlock(), _createBlock(BudgetSummaryCard, {
                                                key: b.categoryName,
                                                categoryName: b.categoryName,
                                                currentSpend: b.currentSpend,
                                                planAmount: b.planAmount,
                                                resetDate: b.resetDate,
                                                onClick: ($event) => (_unref(router).push({ path: '/SpendingPlan', query: { section: b.parentName } }))
                                            }, null, 8, ["categoryName", "currentSpend", "planAmount", "resetDate", "onClick"]));
                                        }), 128))
                                    ], 2))
                                    : (_unref(selectedSubCategory) && subCategoryBudgets.value.length >= 1)
                                        ? (_openBlock(), _createBlock(BudgetSummaryCard, {
                                            key: 2,
                                            categoryName: subCategoryBudgets.value[0].categoryName,
                                            currentSpend: subCategoryBudgets.value[0].currentSpend,
                                            planAmount: subCategoryBudgets.value[0].planAmount,
                                            resetDate: subCategoryBudgets.value[0].resetDate,
                                            onClick: _cache[6] || (_cache[6] = ($event) => (_unref(router).push({ path: '/SpendingPlan', query: { section: subCategoryBudgets.value[0].parentName } })))
                                        }, null, 8, ["categoryName", "currentSpend", "planAmount", "resetDate"]))
                                        : _createCommentVNode("", true)
                        ], 64))
                        : _createCommentVNode("", true)
                ]),
                _createVNode(DateRangeDrawer, {
                    modelValue: dateDrawerOpen.value,
                    "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event) => ((dateDrawerOpen).value = $event)),
                    initialSelection: _unref(dateSelection),
                    payCycleLabel: payCycleLabel.value,
                    onSelect: onDateRangeSelect,
                    onPayCycleClick: _cache[8] || (_cache[8] = ($event) => (_unref(router).push('/setupspendinganalysis')))
                }, null, 8, ["modelValue", "initialSelection", "payCycleLabel"]),
                _createVNode(SuccessToast, {
                    count: categorisedToastCount.value,
                    "onUpdate:count": _cache[9] || (_cache[9] = ($event) => ((categorisedToastCount).value = $event))
                }, null, 8, ["count"]),
                _createVNode(SimpleDrawerPanel, {
                    modelValue: transactionsDrawerOpen.value,
                    "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event) => ((transactionsDrawerOpen).value = $event)),
                    title: transactionsSectionTitle.value,
                    height: "90vh"
                }, {
                    default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_6, [
                            (apiTransactionGroups.value.length > 0)
                                ? (_openBlock(), _createBlock(TransactionsList, {
                                    key: 0,
                                    search: true,
                                    showDateTotal: false,
                                    transactions: apiTransactionGroups.value,
                                    formatter: _unref(state).formatter,
                                    today: _unref(state).today,
                                    yesterday: _unref(state).yesterday,
                                    dividingLines: true,
                                    onTransactionClick: onDrawerTransactionClick,
                                    categoryPills: true,
                                    initialLimit: 100
                                }, null, 8, ["transactions", "formatter", "today", "yesterday"]))
                                : _createCommentVNode("", true)
                        ])
                    ]),
                    _: 1
                }, 8, ["modelValue", "title"])
            ], 64));
        };
    }
});
